import { feedbackStatusResponseSchema, type FeedbackStatusResponse } from '@awwyeah/shared';

/**
 * Per-device conveniences for the feedback box, all best-effort (storage can be missing or
 * throw in private windows): the unsent draft survives a reload, and the last-known status
 * lets the footer link render offline / before the network answers.
 */

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const DRAFT_KEY = 'awwyeah.feedback.draft';
const STATUS_KEY = 'awwyeah.feedback.status';

function storage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readDraft(store: StorageLike | null = storage()): string {
  try {
    return store?.getItem(DRAFT_KEY) ?? '';
  } catch {
    return '';
  }
}

export function writeDraft(text: string, store: StorageLike | null = storage()): void {
  try {
    if (text) store?.setItem(DRAFT_KEY, text);
    else store?.removeItem(DRAFT_KEY);
  } catch {
    // Draft also lives in component state; losing the reload copy is fine.
  }
}

/** Last status seen for `userId` — keyed by member so a shared device never shows the
 * wrong person's link. */
export function readCachedStatus(
  userId: string,
  store: StorageLike | null = storage(),
): FeedbackStatusResponse | null {
  try {
    const raw = store?.getItem(STATUS_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { userId: cachedFor, status } = parsed as { userId?: unknown; status?: unknown };
    if (cachedFor !== userId) return null;
    const result = feedbackStatusResponseSchema.safeParse(status);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function writeCachedStatus(
  userId: string,
  status: FeedbackStatusResponse,
  store: StorageLike | null = storage(),
): void {
  try {
    store?.setItem(STATUS_KEY, JSON.stringify({ userId, status }));
  } catch {
    // Best effort.
  }
}
